'use strict';
import get from 'lodash/get';
import {stateGo} from 'redux-ui-router';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/takeUntil';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {navStates} from '../../app/router';
import {SCOPE_DESTROY} from '../../config/constants/view-events';
import {eventToObservable} from '../../core-utilities/rx-utils';
import {stateGoPostAuth} from '../actions';
import {AuthModalMode} from '../enums/AuthModalMode';
import {InviteLoginStatuses} from '../enums/InviteLoginStatuses';
import {getIsAuthenticated} from '../reducers/state';
import {actions as authModalActions} from '../redux/authModal';
import {getLoginView} from '../router';
import {logger} from './logger';


InvitationLoginController.$inject = [
  '$scope',
  '$ngRedux',
  '$stateParams',
  '$ionicViewSwitcher',
  'OrganizationInviteAccessCodeDomain',
];

/* InvitationLoginController
 * template: ./invitation-login.html
 *
 * This view has several modes to display
 *
 * No Code
 *  No access code was provided. Either immediately redirect to login or warn user and redirect after a few seconds?
 *
 * Initial Loading:
 *  Access code provided, retrieving it. Display loading indicator
 *
 * Valid Code: Prompt Sign(in|up)
 *  Display the auth module for logging in or creating an account
 *
 * Expired Code: Prompt Refresh Code Email
 *  The code has expired, show a button the user can click to request a new code via email
 *
 * ... see the template for full explanation
 */
export function InvitationLoginController(
  $scope,
  $ngRedux,
  $stateParams,
  $ionicViewSwitcher,
  OrganizationInviteAccessCodeDomain,
) {
  const vm = this;
  const enter$ = Observable.of(null);
  const leave$ = eventToObservable($scope, SCOPE_DESTROY);
  const signUp$ = new Subject();
  const login$ = new Subject();
  const user$ = $ngRedux.state$
    .map(state => state.model.user)
    .distinctUntilChanged()
    .filter(user => !!user);
  const auth$ = $ngRedux.state$
    .takeUntil(user$)
    .map(getIsAuthenticated)
    .distinctUntilChanged()
    .filter(isAuthenticated => isAuthenticated);
  const accessCode = $stateParams.accessCode;

  vm.props = {
    signUp: signUp,
    login: login,
    goToLogin: goToLogin,
    requestNewCode: requestNewCode,
    mode: accessCode ? InviteLoginStatuses.LOADING : InviteLoginStatuses.NO_INVITE,
  };

  init();

  function init() {
    leave$.subscribe(() => {
      signUp$.complete();
      login$.complete();
    });

    if (!accessCode) {
      user$
        .takeUntil(leave$)
        .subscribe(() => {
          logger.debug('the user already has a profile, but there was no access code. go to home');
          $ionicViewSwitcher.nextDirection('forward');
          $ngRedux.dispatch(stateGo(navStates.orgsList));
        });

    } else {
      // until user is authenticated, allow user to open AuthModal
      bindLockAction(signUp$, onSignUp);
      bindLockAction(login$, onLogin);

      enter$
        .takeUntil(user$)
        .switchMap(() =>
          OrganizationInviteAccessCodeDomain.$get(accessCode)
            .then(invite => {
              if (!invite) {
                // inform user invite is not found
                // provide link to login view
                return {payload: {mode: InviteLoginStatuses.NO_INVITE}};
              } else if (!inviteIsValid(invite)) {
                // display button to request a new code
                return {payload: {mode: InviteLoginStatuses.EXPIRED_CODE}};
              } else {
                // This path is now handled in-view
                // render in `#hiw-login-container` element
                return {payload: {mode: InviteLoginStatuses.VALID_CODE_NEED_AUTH, invite}};
              }
            })
            .catch((err) => ({payload: {mode: InviteLoginStatuses.ERROR, err}}))
        )
        .subscribe(action => {
          if (action.payload.err) {
            logger.error({err: action.payload.err}, 'Failed to retrieve access code');
          }

          $scope.$evalAsync(() => {
            updateProps({
              mode: action.payload.mode,
              organizationName: get(action, 'payload.invite.organizationName', ''),
              inviterName: get(action, 'payload.invite.inviterName', ''),
            });
          });
        });

      user$
        .takeUntil(leave$)
        .subscribe(() => {
          logger.debug('the user already has a profile, go to the invite confirmation view');
          $ionicViewSwitcher.nextDirection('forward');
          $ngRedux.dispatch(stateGo('invitation-respond', {accessCode}));
        });
    }
  }

  // its effectively expired if there are only 5 minutes left. Should account for small clock sync issues
  function inviteIsValid(invite) {
    return invite.expires ? (invite.expires.getTime() - Date.now()) > 5 * 60 * 1000 : false;
  }

  function requestNewCode() {
    updateProps({
      mode: InviteLoginStatuses.REQUESTING_CODE,
    });
    OrganizationInviteAccessCodeDomain.requestNewCode(accessCode)
      .then(() => updateProps({
        mode: InviteLoginStatuses.REQUESTING_CODE_SUCCESS,
      }))
      .catch(err => {
        if (err) {
          logger.error({err}, 'Failed to request refreshed access code');
        }

        updateProps({
          mode: InviteLoginStatuses.REQUESTING_CODE_ERROR,
        });
      });
  }

  function updateProps(props) {
    vm.props = {
      ...vm.props,
      ...props,
    };
  }

  function signUp() {
    signUp$.next({});
  }

  function login() {
    login$.next({});
  }

  function onSignUp() {
    setPostAuth();
    $ngRedux.dispatch(authModalActions.showModal({
      allowedModes: AuthModalMode.SIGNUP,
      initialMode: AuthModalMode.SIGNUP,
    }));
  }

  function onLogin() {
    setPostAuth();
    $ngRedux.dispatch(authModalActions.showModal({
      allowedModes: AuthModalMode.LOGIN,
    }));
  }

  function setPostAuth() {
    $ngRedux.dispatch(stateGoPostAuth('invitation-respond', {accessCode}));
  }

  function goToLogin() {
    $ionicViewSwitcher.nextDirection('forward');
    $ngRedux.dispatch(stateGo(getLoginView()));
  }

  function bindLockAction(action$, action) {
    action$
      .takeUntil(auth$)
      .subscribe(action);
    action$
      .switchMap(() => leave$)
      .subscribe(() => {
        console.log('hiding the modal due to leaving view');
        $ngRedux.dispatch(authModalActions.hideModal());
      });
  }

}



// WEBPACK FOOTER //
// ./src/js/auth/controllers/InvitationLoginController.js